// The writes this console can make, and their own reading of what came back.
//
// ---------------------------------------------------------------------------
// TWO KINDS OF REQUEST, AND THEY ARE NOT INTERCHANGEABLE
//
// Listing clients, creating a client, amending an activation's identity,
// reading which screens have spoken, and reading or saving a kiosk playlist
// run on the session that is already signed in. Opening an activation,
// publishing a configuration bundle, issuing a tablet code, publishing a
// schedule, uploading a picture, and minting a screen token each carry six
// digits ON THE REQUEST — a current authenticator code, not the inheritance of
// a login. The session reads name a company or a diagnostic integer, or store
// a working rotation that is not yet on a wall; the writes that take a code
// change what a stand collects, who can join it, or what a hall sees. An
// amendment is the other kind: requireAmender, no code on the request.
//
// Until 2026-08-29 the last five took XP_ADMIN_TOKEN and nothing else, and
// this console holds no such thing — README.md says so under what is not built,
// and it is not an omission: a master credential in a browser on a client-facing
// origin is the thing the separate console host exists to prevent. So there was
// no interface for any of them, a crew member's tablet and a screen in a hall
// could only be set up with curl, and neither could be tested by a person.
//
// The service now also accepts a signed-in nova_admin session that presents a
// current authenticator code ON THE REQUEST, which is not the same as a session
// that proved one at sign-in. api/src/auth/freshFactor.ts carries the argument
// and this console does not restate it. What matters here is the consequence:
// there is nowhere in this package that a code is stored, reused, or carried
// from one request to the next. It is typed, sent once, and the field is cleared.
//
// ---------------------------------------------------------------------------
// WHY THIS DOES NOT USE `failureFor`, WHICH IS api/enrol.ts's REASON AGAIN
//
// client.ts maps every 403 to one outcome, `refused`, whose whole meaning is
// "live credential, not permitted" — a dead end the person cannot act on. Three
// of the 403s these routes send are the opposite of a dead end:
//
//   403 second_factor_code_required   — type the code. Nothing is wrong.
//   403 second_factor_code_wrong      — wait for the next one and retype.
//   403 second_factor_unavailable     — this instance cannot check codes at all.
//
// Collapsed into `refused`, the first two would be drawn as "you may not do
// this", which is false and which no button on the screen could resolve. So the
// discrimination is on the `error` field, exactly as api/enrol.ts discriminates
// its two 401s, and for the same reason: a status code that means two opposite
// things has to be read alongside something else or it will be read wrongly.
//
// THE 401 IS STILL A DEAD SESSION AND IS STILL OBEYED. Unlike the enrolment
// confirmation, none of these routes answers 401 for a wrong code — the service
// deliberately sends 403 there, so that a mistyped digit cannot sign an admin
// out. That is a property of the service rather than of this file, and the
// section in check.mjs that drives a wrong code asserts the session survives it,
// which is what would notice if the service ever changed its mind.
//
// ---------------------------------------------------------------------------
// NOTHING HERE JUDGES A CODE, A CONTENT TYPE, A SIZE OR A SCHEDULE
//
// No length test on the six digits, no allow-list of image types, no byte
// ceiling, and no opinion about whether a schedule may be published. Every one
// of those is a rule, every one is written down on the service — totp.ts,
// kiosk/assets.ts's `refuseAsset`, and the kiosk module's own `publish` — and a
// copy here would be a second opinion that disagrees after the next change.
// README.md's first rule: the console renders decisions, it does not make them.
//
// The one thing this file does refuse is a response it could not read, and that
// is not a rule about the domain — it is a refusal to draw half an answer. An
// enrolment code shown without its expiry is a code a crew member types after it
// has died, with nothing on the screen that could have told them.

import {
  PEOPLE_ENDPOINTS,
  type AttendeeActionKind,
  type ClientSortKey,
  type CorrectableField,
} from "../../../api/src/admin/endpoints.js";
import { ADMIN_ENDPOINTS } from "../../../api/src/identity/endpoints.js";
import { KIOSK_ENDPOINTS } from "../../../api/src/kiosk/endpoints.js";
import { PUBLISH_ENDPOINTS } from "../../../api/src/publish/endpoints.js";
// endpoints.js, NOT freshFactor.js, WHICH IS WHERE THIS IMPORT POINTED UNTIL
// 2026-08-30 AND IS THE REASON THIS PACKAGE COULD NOT BE BUILT. Both modules
// export this name. Only one of them imports nothing: freshFactor.ts reaches
// db.js and secondFactor.js, and through the latter `node:crypto`, so importing
// a string from it put login-code and TOTP source in a browser build's graph and
// vite refused the build. The rule the other four imports in this file already
// follow: a browser package reads names from a module that imports nothing.
import { FRESH_FACTOR_FIELD } from "../../../api/src/auth/endpoints.js";
import { authenticated, type Answer, type ApiOptions } from "./client.js";
import type { SessionToken } from "../session/store.js";

export { ADMIN_ENDPOINTS, KIOSK_ENDPOINTS, PEOPLE_ENDPOINTS, PUBLISH_ENDPOINTS, FRESH_FACTOR_FIELD };

export type IdentityProfile = {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly phone: string | null;
  readonly jobTitle: string | null;
};

export type LiveIdentity = {
  readonly identityId: string;
  readonly email: string;
  readonly role: "nova_admin" | "client_viewer";
  readonly clientId: string;
} & IdentityProfile;

/** What a picker prints: the name when we have one, otherwise the mailbox. */
export function personOptionLabel(identity: LiveIdentity): string {
  const name = [identity.firstName, identity.lastName].filter((part) => part !== null && part !== "").join(" ");
  return name === "" ? identity.email : `${name} (${identity.email})`;
}

export async function listIdentities(
  options: ApiOptions,
  token: SessionToken,
  clientId: string,
): Promise<Attempt<{ readonly identities: readonly LiveIdentity[] }>> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.listIdentities}?client_id=${encodeURIComponent(clientId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const raw = fields(answer.body)["identities"];
  if (!Array.isArray(raw)) return { ok: false, ...unreadable("identity list", "identities") };

  const identities: LiveIdentity[] = [];
  for (const entry of raw) {
    const row = fields(entry);
    const identityId = text(row["identity_id"]);
    const email = text(row["email"]);
    const role = row["role"] === "nova_admin" || row["role"] === "client_viewer" ? row["role"] : null;
    const atClient = text(row["client_id"]);
    if (identityId === null || email === null || role === null || atClient === null) {
      return { ok: false, ...unreadable("identity list", "identity_id, email, role and client_id") };
    }
    identities.push({
      identityId,
      email,
      role,
      clientId: atClient,
      firstName: text(row["first_name"]),
      lastName: text(row["last_name"]),
      phone: text(row["phone"]),
      jobTitle: text(row["job_title"]),
    });
  }
  return { ok: true, value: { identities } };
}

export type CreatedIdentity = {
  readonly identityId: string;
  readonly email: string;
  readonly role: "nova_admin" | "client_viewer";
  /** Null for a nova_admin. 006 gives a Nova person no client, deliberately. */
  readonly clientId: string | null;
} & IdentityProfile;

/**
 * Put a person at a client on the books.
 *
 * A session write, like creating a client: no fresh authenticator code, because
 * this hands out no access. An identity is a name that can be referred to; what
 * lets somebody read a report is a grant, which is a different write with a
 * different ceiling. admin/instruction.ts leans on exactly that when it refuses
 * an instruction — "the person at the client has to exist here first" — and
 * until this function existed there was no way to do that from a browser.
 *
 * `client_id` is sent rather than omitted, including as null. The service takes
 * it as optional-and-nullable and says why: a console omits it for an admin and
 * a form that always sends the field sends null, and refusing the second would
 * be refusing a well-formed request for a reason no message could make clear.
 */
export async function createIdentity(
  options: ApiOptions,
  token: SessionToken,
  input: {
    email: string;
    role: "nova_admin" | "client_viewer";
    clientId: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    jobTitle: string | null;
  },
): Promise<Attempt<CreatedIdentity>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.createIdentity,
    method: "POST",
    json: {
      email: input.email,
      role: input.role,
      client_id: input.clientId,
      first_name: input.firstName,
      last_name: input.lastName,
      phone: input.phone,
      job_title: input.jobTitle,
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const read = readIdentityBody(answer.body);
  if (read === null) return { ok: false, ...unreadable("identity", "identity_id, email and role") };
  return { ok: true, value: read };
}

export type CorrectedIdentity = CreatedIdentity;

/**
 * Correct the name on a person already on the books.
 *
 * Email is not sent. The mailbox is how they sign in; changing it would be a
 * different person receiving the code. Same session write as createIdentity:
 * no authenticator code, because this still hands out no access.
 */
export async function correctIdentity(
  options: ApiOptions,
  token: SessionToken,
  identityId: string,
  input: {
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    jobTitle: string | null;
  },
): Promise<Attempt<CorrectedIdentity>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.correctIdentity.replace(":identityId", identityId),
    method: "PATCH",
    json: {
      first_name: input.firstName,
      last_name: input.lastName,
      phone: input.phone,
      job_title: input.jobTitle,
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const read = readIdentityBody(answer.body);
  if (read === null) return { ok: false, ...unreadable("identity", "identity_id, email and role") };
  return { ok: true, value: read };
}

function readIdentityBody(body: unknown): CreatedIdentity | null {
  const b = fields(body);
  const identityId = text(b["identity_id"]);
  const email = text(b["email"]);
  const role = b["role"] === "nova_admin" || b["role"] === "client_viewer" ? b["role"] : null;
  if (identityId === null || email === null || role === null) return null;
  return {
    identityId,
    email,
    role,
    clientId: text(b["client_id"]),
    firstName: text(b["first_name"]),
    lastName: text(b["last_name"]),
    phone: text(b["phone"]),
    jobTitle: text(b["job_title"]),
  };
}

export type RecordedInstruction = {
  readonly instructionId: string;
  readonly activationId: string;
  readonly clientId: string;
  readonly fieldSetVersion: number;
  readonly givenAt: string;
  readonly instructedIdentityId: string;
  readonly selectionHash: string;
  /**
   * The block a bundle has to carry, as the service assembled it.
   *
   * Pasted into compileConfigBundle. Rebuilding it from instruction_id and
   * selection_hash is the retype the instruction route exists to prevent.
   */
  readonly citeInBundle: { readonly instruction_id: string; readonly selection_hash: string };
};

/**
 * Record what a named person at the client instructed.
 *
 * A session write, like listing clients: no fresh authenticator code and no
 * XP_ADMIN_TOKEN. Publishing a bundle that cites this is a different request:
 * the same session, plus six digits on that POST. This function does not send
 * them, and this console still does not hold the admin token.
 */
export async function recordInstruction(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    instructedIdentityId: string;
    fieldSetVersion: number;
    selection: unknown;
  },
): Promise<Attempt<RecordedInstruction>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.recordInstruction,
    method: "POST",
    json: {
      activation_id: input.activationId,
      instructed_identity_id: input.instructedIdentityId,
      field_set_version: input.fieldSetVersion,
      selection: input.selection,
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const instructionId = text(b["instruction_id"]);
  const activationId = text(b["activation_id"]);
  const clientId = text(b["client_id"]);
  const fieldSetVersion = whole(b["field_set_version"]);
  const givenAt = text(b["given_at"]);
  const instructedIdentityId = text(b["instructed_identity_id"]);
  const selectionHash = text(b["selection_hash"]);
  const cited = fields(fields(b["cite_in_bundle"])["client_instruction"]);
  const citedId = text(cited["instruction_id"]);
  const citedHash = text(cited["selection_hash"]);
  if (
    instructionId === null ||
    activationId === null ||
    clientId === null ||
    fieldSetVersion === null ||
    givenAt === null ||
    instructedIdentityId === null ||
    selectionHash === null ||
    citedId === null ||
    citedHash === null
  ) {
    return {
      ok: false,
      ...unreadable(
        "instruction",
        "instruction_id, activation_id, client_id, field_set_version, given_at, instructed_identity_id, selection_hash and cite_in_bundle.client_instruction",
      ),
    };
  }
  return {
    ok: true,
    value: {
      instructionId,
      activationId,
      clientId,
      fieldSetVersion,
      givenAt,
      instructedIdentityId,
      selectionHash,
      citeInBundle: { instruction_id: citedId, selection_hash: citedHash },
    },
  };
}

export type ListedInstruction = RecordedInstruction & {
  readonly requiredFields: readonly string[];
  /**
   * The offer a later visit redraws. Null where an older service has not sent
   * them yet: the console and the API deploy separately, and refusing the whole
   * list for a missing snapshot would hide a citation Publish Live still needs.
   */
  readonly fieldIds: readonly string[] | null;
  readonly purposes: readonly string[] | null;
  readonly contact: Readonly<Record<string, boolean>> | null;
};

export type InstructionList = {
  readonly instructions: readonly ListedInstruction[];
  readonly nextFieldSetVersion: number;
  readonly onsiteReset: {
    readonly idleHomeMs: number;
    readonly crewResetEnabled: boolean;
  } | null;
};

/**
 * The recorded setups for one activation, and the next unused field-set version.
 *
 * GET on the same path recordInstruction posts to. A later visit to Legal copy
 * has to see the last citation or Publish Live asks for a setup that already
 * exists.
 */
export async function listInstructions(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
): Promise<Attempt<InstructionList>> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.recordInstruction}?activation_id=${encodeURIComponent(activationId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const body = fields(answer.body);
  const nextFieldSetVersion = whole(body["next_field_set_version"]);
  const idleHomeMs = whole(body["onsite_idle_home_ms"]);
  const crewResetEnabled = body["onsite_crew_reset_enabled"];
  const raw = body["instructions"];
  if (!Array.isArray(raw) || nextFieldSetVersion === null) {
    return { ok: false, ...unreadable("instruction list", "instructions and next_field_set_version") };
  }

  const instructions: ListedInstruction[] = [];
  for (const entry of raw) {
    const row = fields(entry);
    const instructionId = text(row["instruction_id"]);
    const rowActivationId = text(row["activation_id"]);
    const clientId = text(row["client_id"]);
    const fieldSetVersion = whole(row["field_set_version"]);
    const givenAt = text(row["given_at"]);
    const instructedIdentityId = text(row["instructed_identity_id"]);
    const selectionHash = text(row["selection_hash"]);
    const cited = fields(fields(row["cite_in_bundle"])["client_instruction"]);
    const citedId = text(cited["instruction_id"]);
    const citedHash = text(cited["selection_hash"]);
    const required = row["required_fields"];
    if (
      instructionId === null ||
      rowActivationId === null ||
      clientId === null ||
      fieldSetVersion === null ||
      givenAt === null ||
      instructedIdentityId === null ||
      selectionHash === null ||
      citedId === null ||
      citedHash === null ||
      !Array.isArray(required) ||
      required.some((id) => typeof id !== "string")
    ) {
      return {
        ok: false,
        ...unreadable(
          "instruction list",
          "instruction_id, activation_id, client_id, field_set_version, given_at, instructed_identity_id, selection_hash, required_fields and cite_in_bundle.client_instruction",
        ),
      };
    }
    instructions.push({
      instructionId,
      activationId: rowActivationId,
      clientId,
      fieldSetVersion,
      givenAt,
      instructedIdentityId,
      selectionHash,
      requiredFields: required.filter((id): id is string => typeof id === "string"),
      fieldIds: stringListOrNull(row["field_ids"]),
      purposes: stringListOrNull(row["purposes"]),
      contact: booleanMapOrNull(row["contact"]),
      citeInBundle: { instruction_id: citedId, selection_hash: citedHash },
    });
  }
  return {
    ok: true,
    value: {
      instructions,
      nextFieldSetVersion,
      onsiteReset:
        idleHomeMs === null || typeof crewResetEnabled !== "boolean"
          ? null
          : { idleHomeMs, crewResetEnabled },
    },
  };
}

/**
 * A company, with how much is on it.
 *
 * THE THREE FIGURES ARE NULLABLE, AND NOT BECAUSE THE SERVICE MAY OMIT THEM.
 * It always sends them. The console and the API are two deployments and they go
 * out separately, so between the two there is a window where this bundle is
 * asking an older service that has never heard of them — and the two ways of
 * handling that are both worse than a null. Refusing the whole list turns a
 * cosmetic gap into an unusable Clients page for the length of a deploy.
 * Defaulting to zero prints "0 activations" beside a company with four, which is
 * not a degraded answer but a wrong one, and nothing on the screen would say so.
 * Null is the third thing: the column stays, the cell says it does not know, and
 * nobody reads a number that was never sent.
 */
/**
 * A company, named and nothing more.
 *
 * What creating one and correcting one answer with. Separate from LiveClient
 * rather than the same type with the figures left null, because these two calls
 * are not a directory read and never had the figures to omit: a create returns
 * the row it wrote. Sharing the type would put three nulls on every created
 * client and make "the service did not send them" indistinguishable from "this
 * was never that kind of answer".
 */
export type NamedClient = { readonly clientId: string; readonly name: string };

export type LiveClient = NamedClient & {
  readonly activations: number | null;
  readonly open: number | null;
  /**
   * Completed registrations across this client's shows, each show counted once
   * per person.
   *
   * NOT A COUNT OF PEOPLE, and the screen must not label it as one. The service
   * cannot tell that somebody at two of a client's shows is one person —
   * attendee_ref is unique within an activation and never across — so this is
   * the sum of per-show figures and somebody with a following is counted twice.
   * api/src/admin/people.ts carries the long form.
   */
  readonly registrations: number | null;
  readonly agreement: ClientAgreement;
};

/**
 * Whether a file may be produced for this client, and on what basis.
 *
 * "unknown" is this desk's, not the service's: it is what a listing that did not
 * carry an agreement at all parses to, which happens when the console is ahead
 * of the API. The screen draws it as an em-dash rather than as "none", because
 * telling somebody their client has no processing agreement when the truth is
 * that this build cannot see one would send them to record a document that is
 * already on file.
 */
export type ClientAgreement = {
  readonly state: "recorded" | "grandfathered" | "none" | "unknown";
  readonly signedOn: string | null;
  readonly retentionMonths: number | null;
  readonly noticesPerson: string | null;
  readonly noticesEmail: string | null;
};

function agreementFrom(value: unknown): ClientAgreement {
  const row = value === null || typeof value !== "object" ? null : (value as Record<string, unknown>);
  const state = row === null ? null : text(row["state"]);
  return {
    // Only the three the service defines. A fourth word arriving from a newer
    // API reads as unknown rather than being passed through to a screen that
    // has no way to draw it.
    state: state === "recorded" || state === "grandfathered" || state === "none" ? state : "unknown",
    signedOn: row === null ? null : text(row["signed_on"]),
    retentionMonths: row === null ? null : integerCount(row["retention_months"]),
    noticesPerson: row === null ? null : text(row["notices_person"]),
    noticesEmail: row === null ? null : text(row["notices_email"]),
  };
}

/**
 * The client list, in the service's order or in one a reader chose.
 *
 * `sort` is the column and direction the Clients table's heads hold, sent as
 * `?sort=&dir=` for the service to apply (12 Oct hand-off: every column sorts,
 * on the server). Null sends nothing at all, so the plain read — the one Open a
 * show makes, and the first one Clients makes — is byte for byte the request it
 * always was, and the service stays the one place that knows the default order.
 */
/** One client Add activation can open a show for: a name and an id. */
export type ClientChoice = { readonly clientId: string; readonly name: string };

/**
 * The live clients this reader can add a show to (12 Oct): every one for an
 * Admin, the ones they hold for an Editor. Add activation's picker, and only
 * that: the Clients page's directory, with its counts and agreements, is
 * listClients and an Admin's.
 */
export async function listClientChoices(
  options: ApiOptions,
  token: SessionToken,
): Promise<Attempt<{ readonly clients: readonly ClientChoice[] }>> {
  const answer = await authenticated(options, token, { path: PEOPLE_ENDPOINTS.clientChoices, method: "GET" });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const raw = fields(answer.body)["clients"];
  if (!Array.isArray(raw)) return { ok: false, ...unreadable("client list", "clients") };
  const clients: ClientChoice[] = [];
  for (const entry of raw) {
    const row = fields(entry);
    const clientId = text(row["client_id"]);
    const name = text(row["name"]);
    if (clientId === null || name === null) return { ok: false, ...unreadable("client list", "client_id and name") };
    clients.push({ clientId, name });
  }
  return { ok: true, value: { clients } };
}

export async function listClients(
  options: ApiOptions,
  token: SessionToken,
  sort: { readonly key: ClientSortKey; readonly dir: "asc" | "desc" } | null = null,
): Promise<Attempt<{ readonly clients: readonly LiveClient[] }>> {
  const order = sort === null ? "" : `?sort=${encodeURIComponent(sort.key)}&dir=${sort.dir === "desc" ? "desc" : "asc"}`;
  const answer = await authenticated(options, token, { path: `${PEOPLE_ENDPOINTS.listClients}${order}`, method: "GET" });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const raw = fields(answer.body)["clients"];
  if (!Array.isArray(raw)) return { ok: false, ...unreadable("client list", "clients") };

  const clients: LiveClient[] = [];
  for (const entry of raw) {
    const row = fields(entry);
    const clientId = text(row["client_id"]);
    const name = text(row["name"]);
    if (clientId === null || name === null) return { ok: false, ...unreadable("client list", "client_id and name") };
    // integerCount and not a cast: it is the helper this file already uses for
    // the trouble column, with the same rule and the same reason. Anything that
    // is not a whole number at or above zero reads as "not a count" and is drawn
    // as that rather than rounded into something plausible.
    clients.push({
      clientId,
      name,
      activations: integerCount(row["activations"]),
      open: integerCount(row["open"]),
      registrations: integerCount(row["registrations"]),
      agreement: agreementFrom(row["agreement"]),
    });
  }
  return { ok: true, value: { clients } };
}

export async function createClient(
  options: ApiOptions,
  token: SessionToken,
  name: string,
): Promise<Attempt<NamedClient>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.createClient,
    method: "POST",
    json: { name },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const clientId = text(b["client_id"]);
  const created = text(b["name"]);
  if (clientId === null || created === null) {
    return { ok: false, ...unreadable("client", "client_id and name") };
  }
  return { ok: true, value: { clientId, name: created } };
}

export async function correctClient(
  options: ApiOptions,
  token: SessionToken,
  clientId: string,
  name: string,
): Promise<Attempt<NamedClient>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.correctClient.replace(":clientId", clientId),
    method: "PATCH",
    json: { name },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const id = text(b["client_id"]);
  const corrected = text(b["name"]);
  if (id === null || corrected === null) {
    return { ok: false, ...unreadable("client", "client_id and name") };
  }
  return { ok: true, value: { clientId: id, name: corrected } };
}

/**
 * Put a signed processing agreement on file.
 *
 * The write that lets an export run at all. Everything about whether the date is
 * a real day, whether it is in the future and whether the retention is sane is
 * the service's — this sends what was typed and draws the sentence that comes
 * back. A second copy of those rules here would be a second thing to keep in
 * step, and the one that mattered would still be the far one.
 */
export async function recordAgreement(
  options: ApiOptions,
  token: SessionToken,
  clientId: string,
  agreement: {
    readonly signedOn: string;
    readonly retentionMonths: number;
    readonly noticesPerson: string | null;
    readonly noticesEmail: string | null;
  },
): Promise<Attempt<ClientAgreement>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.recordAgreement.replace(":clientId", clientId),
    method: "POST",
    json: {
      signed_on: agreement.signedOn,
      retention_months: agreement.retentionMonths,
      notices_person: agreement.noticesPerson,
      notices_email: agreement.noticesEmail,
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const recorded = agreementFrom(b["agreement"]);
  // A write that answered without saying what it wrote is not readable. The
  // state has to be one of the service's words: "unknown" here would mean this
  // build could not understand its own write, and drawing the old row back
  // would tell somebody the agreement did not land when it may well have.
  if (recorded.state === "unknown") return { ok: false, ...unreadable("agreement", "the agreement it recorded") };
  return { ok: true, value: recorded };
}

export type ArchivedClient = { readonly clientId: string; readonly archivedAt: string };

export async function archiveClient(
  options: ApiOptions,
  token: SessionToken,
  clientId: string,
): Promise<Attempt<ArchivedClient>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.archiveClient.replace(":clientId", clientId),
    method: "POST",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const id = text(b["client_id"]);
  const archivedAt = text(b["archived_at"]);
  if (id === null || archivedAt === null) {
    return { ok: false, ...unreadable("archive", "client_id and archived_at") };
  }
  return { ok: true, value: { clientId: id, archivedAt } };
}

export type OpenedActivation = {
  readonly activationId: string;
  readonly clientId: string;
  readonly name: string;
};

export async function createActivation(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    clientId: string;
    name: string;
    timeZone: string;
    venue?: string;
    opensOn?: string;
    closesOn?: string;
    secondFactorCode: string;
  },
): Promise<Attempt<OpenedActivation>> {
  const answer = await authenticated(options, token, {
    path: PUBLISH_ENDPOINTS.createActivation,
    method: "POST",
    json: {
      activation_id: input.activationId,
      client_id: input.clientId,
      name: input.name,
      time_zone: input.timeZone,
      ...(input.venue === undefined || input.venue === "" ? {} : { venue: input.venue }),
      ...(input.opensOn === undefined || input.opensOn === "" ? {} : { opens_on: input.opensOn }),
      ...(input.closesOn === undefined || input.closesOn === "" ? {} : { closes_on: input.closesOn }),
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const activationId = text(b["activation_id"]);
  const clientId = text(b["client_id"]);
  const name = text(b["name"]);
  if (activationId === null || clientId === null || name === null) {
    return { ok: false, ...unreadable("activation", "activation_id, client_id and name") };
  }
  return { ok: true, value: { activationId, clientId, name } };
}

export type AmendedActivation = {
  readonly amendmentId: string;
  readonly activationId: string;
  readonly clientId: string;
};

/**
 * Restate facts on an activation that is already open.
 *
 * Session path: omit `amended_by`, and do not send a TOTP field. requireAmender
 * is a nova_admin session that already proved a second factor at login. A
 * caller who names `amended_by` on a session is refused; a caller who sends
 * six digits is sending a field the schema does not declare.
 */
export async function readLegalDraft(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
): Promise<Attempt<{ draft: unknown | null }>> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.legalDraft}?activation_id=${encodeURIComponent(activationId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const b = fields(answer.body);
  if (!Object.prototype.hasOwnProperty.call(b, "draft") && text(b["draft_id"]) === null) {
    return { ok: true, value: { draft: null } };
  }
  return { ok: true, value: { draft: b["draft"] ?? null } };
}

export type EmailEventLine = {
  readonly at: string;
  readonly event: string;
  readonly to: string;
};

export type EmailProviderReport = {
  readonly provider: "mailgun" | "resend";
  readonly configured: boolean;
  readonly domain: string | null;
  readonly window: string;
  readonly accepted: number | null;
  readonly delivered: number | null;
  readonly failed: number | null;
  readonly opened: number | null;
  readonly complained: number | null;
  readonly recent: readonly EmailEventLine[];
  readonly note: string | null;
};

export type EmailStatsReport = {
  readonly mailgun: EmailProviderReport;
  readonly resend: EmailProviderReport;
};

export async function readEmailStats(
  options: ApiOptions,
  token: SessionToken,
): Promise<Attempt<EmailStatsReport>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.emailStats,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const body = fields(answer.body);
  const mailgun = providerReport(body["mailgun"], "mailgun");
  const resend = providerReport(body["resend"], "resend");
  if (mailgun === null || resend === null) {
    return { ok: false, ...unreadable("email stats", "mailgun and resend") };
  }
  return { ok: true, value: { mailgun, resend } };
}

export type SiteStatsReport = {
  readonly configured: boolean;
  readonly site: string | null;
  readonly window: string;
  readonly visitors: number | null;
  readonly visits: number | null;
  readonly pageviews: number | null;
  readonly bounce_rate: number | null;
  readonly note: string | null;
};

export async function readSiteStats(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
  /** The venue's days Today read, or absent for everything the site holds. */
  days?: { readonly from: string; readonly to: string } | null,
): Promise<Attempt<SiteStatsReport>> {
  const range =
    days === undefined || days === null
      ? ""
      : `&from=${encodeURIComponent(days.from)}&to=${encodeURIComponent(days.to)}`;
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.siteStats}?activation_id=${encodeURIComponent(activationId)}${range}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const row = fields(answer.body);
  return {
    ok: true,
    value: {
      configured: row["configured"] === true,
      site: text(row["site"]),
      window: text(row["window"]) ?? "",
      visitors: numberOrNull(row["visitors"]),
      visits: numberOrNull(row["visits"]),
      pageviews: numberOrNull(row["pageviews"]),
      bounce_rate: numberOrNull(row["bounce_rate"]),
      note: text(row["note"]),
    },
  };
}

export type RideSponsorBillboard = {
  readonly assetId: string;
  readonly kind: "hoarding" | "banner";
  readonly weight: number;
  readonly url: string | null;
};

export type RideSponsorReport = {
  readonly siteEnabled: boolean;
  readonly gameEnabled: boolean;
  readonly name: string;
  readonly logoLightUrl: string | null;
  readonly logoDarkUrl: string | null;
  readonly logoLightAssetId: string | null;
  readonly logoDarkAssetId: string | null;
  readonly billboards: readonly RideSponsorBillboard[];
};

export async function readRideSponsor(
  options: ApiOptions,
  token: SessionToken,
  activationId: string,
): Promise<Attempt<RideSponsorReport>> {
  const answer = await authenticated(options, token, {
    path: `${PEOPLE_ENDPOINTS.rideSponsor}?activation_id=${encodeURIComponent(activationId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const read = rideSponsorFrom(answer.body);
  if (read === null) return { ok: false, ...unreadable("sponsor branding", "site_enabled and game_enabled") };
  return { ok: true, value: read };
}

export async function writeRideSponsor(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    siteEnabled: boolean;
    gameEnabled: boolean;
    name: string;
    logoLightAssetId: string | null;
    logoDarkAssetId: string | null;
    billboards: readonly { assetId: string; kind: "hoarding" | "banner"; weight: number }[];
  },
): Promise<Attempt<RideSponsorReport>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.rideSponsor,
    method: "PUT",
    json: {
      activation_id: input.activationId,
      site_enabled: input.siteEnabled,
      game_enabled: input.gameEnabled,
      name: input.name,
      logo_light_asset_id: input.logoLightAssetId,
      logo_dark_asset_id: input.logoDarkAssetId,
      billboards: input.billboards.map((row) => ({
        asset_id: row.assetId,
        kind: row.kind,
        weight: row.weight,
      })),
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const read = rideSponsorFrom(answer.body);
  if (read === null) return { ok: false, ...unreadable("sponsor branding", "site_enabled and game_enabled") };
  return { ok: true, value: read };
}

export async function uploadRideSponsorAsset(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; contentType: string; bytesBase64: string; sha256: string | null },
): Promise<Attempt<{ assetId: string; alreadyHeld: boolean }>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.rideSponsorAsset,
    method: "POST",
    json: {
      activation_id: input.activationId,
      content_type: input.contentType,
      bytes_base64: input.bytesBase64,
      ...(input.sha256 === null ? {} : { sha256: input.sha256 }),
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const row = fields(answer.body);
  const assetId = text(row["asset_id"]);
  if (assetId === null) return { ok: false, ...unreadable("sponsor picture", "asset_id") };
  return { ok: true, value: { assetId, alreadyHeld: row["already_held"] === true } };
}

function rideSponsorFrom(body: unknown): RideSponsorReport | null {
  const row = fields(body);
  if (typeof row["site_enabled"] !== "boolean" || typeof row["game_enabled"] !== "boolean") return null;
  const ids = Array.isArray(row["billboard_ids"]) ? row["billboard_ids"] : [];
  const published = Array.isArray(row["billboards"]) ? row["billboards"] : [];
  const billboards: RideSponsorBillboard[] = ids.flatMap((item) => {
    const one = fields(item);
    const assetId = text(one["asset_id"]);
    const kind = one["kind"] === "banner" ? "banner" : one["kind"] === "hoarding" ? "hoarding" : null;
    if (assetId === null || kind === null) return [];
    const match = published.find((entry) => fields(entry)["asset_id"] === assetId);
    return [
      {
        assetId,
        kind,
        weight: whole(one["weight"]) ?? 1,
        url: text(fields(match)["url"]),
      },
    ];
  });
  return {
    siteEnabled: row["site_enabled"] === true,
    gameEnabled: row["game_enabled"] === true,
    name: text(row["name"]) ?? "",
    logoLightUrl: text(row["logo_light_url"]),
    logoDarkUrl: text(row["logo_dark_url"]),
    logoLightAssetId: text(row["logo_light_asset_id"]),
    logoDarkAssetId: text(row["logo_dark_asset_id"]),
    billboards,
  };
}

function providerReport(value: unknown, provider: "mailgun" | "resend"): EmailProviderReport | null {
  const row = fields(value);
  if (Object.keys(row).length === 0 && value !== null && typeof value !== "object") return null;
  const recent = Array.isArray(row["recent"])
    ? row["recent"].flatMap((item) => {
        const line = fields(item);
        const event = text(line["event"]);
        if (event === null) return [];
        return [{ at: text(line["at"]) ?? "", event, to: text(line["to"]) ?? "an address" }];
      })
    : [];
  return {
    provider,
    configured: row["configured"] === true,
    domain: text(row["domain"]),
    window: text(row["window"]) ?? "",
    accepted: numberOrNull(row["accepted"]),
    delivered: numberOrNull(row["delivered"]),
    failed: numberOrNull(row["failed"]),
    opened: numberOrNull(row["opened"]),
    complained: numberOrNull(row["complained"]),
    recent,
    note: text(row["note"]),
  };
}

function numberOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export async function saveLegalDraft(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; draft: unknown },
): Promise<Attempt<{ draftId: string }>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.legalDraft,
    method: "PUT",
    json: { activation_id: input.activationId, draft: input.draft },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const draftId = text(fields(answer.body)["draft_id"]);
  if (draftId === null) return { ok: false, ...unreadable("legal draft", "draft_id") };
  return { ok: true, value: { draftId } };
}

export async function amendActivation(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    clientId: string;
    name?: string;
    venue?: string;
    timeZone?: string;
    opensOn?: string;
    closesOn?: string;
    territory?: "UK" | "US";
    openingHours?: readonly { on_date: string; opens_at: string; closes_at: string }[];
  },
): Promise<Attempt<AmendedActivation>> {
  const answer = await authenticated(options, token, {
    path: PUBLISH_ENDPOINTS.amendActivation,
    method: "POST",
    json: {
      activation_id: input.activationId,
      client_id: input.clientId,
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(input.venue === undefined ? {} : { venue: input.venue }),
      ...(input.timeZone === undefined ? {} : { time_zone: input.timeZone }),
      ...(input.opensOn === undefined ? {} : { opens_on: input.opensOn }),
      ...(input.closesOn === undefined ? {} : { closes_on: input.closesOn }),
      ...(input.territory === undefined ? {} : { territory: input.territory }),
      ...(input.openingHours === undefined ? {} : { opening_hours: input.openingHours }),
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const amendmentId = text(b["amendment_id"]);
  const activationId = text(b["activation_id"]);
  const clientId = text(b["client_id"]);
  if (amendmentId === null || activationId === null || clientId === null) {
    return { ok: false, ...unreadable("amendment", "amendment_id, activation_id and client_id") };
  }
  return { ok: true, value: { amendmentId, activationId, clientId } };
}

export type ActivationClosure = {
  /** What it is now, which is what the caller asked for or the request failed. */
  readonly closed: boolean;
  /** When it was recorded as finished, or null once it is reopened. */
  readonly closedAt: string | null;
};

/**
 * Record that a show has finished, or take that back.
 *
 * NOT A SEAL, AND THE ONE PLACE THAT MATTERS IS THE SENTENCE THIS SCREEN PUTS
 * NEXT TO IT. A close says the show is over and does not stop anything being
 * captured: a tablet that was offline across the last day still syncs what it
 * holds afterwards, and those events arrive and are counted as late. Sealing is
 * the write that ends and cannot be undone, and nothing on this console reaches
 * it. See PUBLISH_ENDPOINTS.closeActivation for why both directions are one
 * route with a boolean rather than a `/close` beside a `/reopen`.
 */
export async function closeActivation(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; clientId: string; closed: boolean; closedBy?: string },
): Promise<Attempt<ActivationClosure>> {
  const answer = await authenticated(options, token, {
    path: PUBLISH_ENDPOINTS.closeActivation,
    method: "POST",
    json: {
      activation_id: input.activationId,
      client_id: input.clientId,
      // Always sent, never inferred from which button was pressed. The service
      // requires it for the same reason: a request that lost this field must be
      // refused rather than guessed at, and closing is the expensive guess.
      closed: input.closed,
      ...(input.closedBy === undefined ? {} : { closed_by: input.closedBy }),
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const closed = b["closed"];
  if (typeof closed !== "boolean") {
    return { ok: false, ...unreadable("closure", "closed") };
  }
  return { ok: true, value: { closed, closedAt: text(b["closed_at"]) } };
}

/** What the service says a show's pause now is. */
export type ActivationPause = {
  readonly paused: boolean;
  /** When it was paused, or null once it is resumed. */
  readonly pausedAt: string | null;
};

/**
 * Pause a show, or resume it (151, decision C3).
 *
 * The closure's shape: one route and a boolean, both directions reversible. An
 * Admin's, and an Editor's for a client they hold; the service refuses a
 * Viewer, a sealed show and a show already in the state asked for.
 */
export async function pauseActivation(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; clientId: string; paused: boolean },
): Promise<Attempt<ActivationPause>> {
  const answer = await authenticated(options, token, {
    path: PUBLISH_ENDPOINTS.pauseActivation,
    method: "POST",
    json: { activation_id: input.activationId, client_id: input.clientId, paused: input.paused },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const paused = b["paused"];
  if (typeof paused !== "boolean") return { ok: false, ...unreadable("pause", "paused") };
  return { ok: true, value: { paused, pausedAt: text(b["paused_at"]) } };
}

/** Where a person stands after an Admin's action, as the service says. */
export type AttendeeStandingAnswer = {
  readonly withheld: boolean;
  readonly blocked: boolean;
  readonly erased: boolean;
  readonly corrected: boolean;
};

/**
 * One action on one person's record (152, decision C4): withhold, release,
 * block, unblock, correct or erase. An Admin's alone, and every request carries
 * a fresh authenticator code, which the service spends.
 */
export async function actOnAttendee(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    attendeeRef: string;
    action: AttendeeActionKind;
    reason: string | null;
    correction?: Partial<Record<CorrectableField, string | null>>;
    secondFactorCode: string;
  },
): Promise<Attempt<{ readonly standing: AttendeeStandingAnswer; readonly devicesTold: number | null }>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.attendeeActions,
    method: "POST",
    json: {
      activation_id: input.activationId,
      attendee_ref: input.attendeeRef,
      action: input.action,
      ...(input.reason === null ? {} : { reason: input.reason }),
      ...(input.correction === undefined ? {} : { correction: input.correction }),
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const standing = fields(b["standing"]);
  if (typeof standing["withheld"] !== "boolean") return { ok: false, ...unreadable("person action", "standing") };
  const erasure = fields(b["erasure"]);
  return {
    ok: true,
    value: {
      standing: {
        withheld: standing["withheld"] === true,
        blocked: standing["blocked"] === true,
        erased: standing["erased"] === true,
        corrected: standing["corrected"] === true,
      },
      devicesTold: typeof erasure["devices_told"] === "number" ? erasure["devices_told"] : null,
    },
  };
}

/**
 * Ask the game service again about an erasure's Ride part (153), where the
 * first attempt did not land. An Admin's; no fresh code, because nothing new
 * is decided.
 */
export async function retryRideErasure(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; attendeeRef: string },
): Promise<Attempt<{ readonly rideState: string }>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.attendeeErasureRetries,
    method: "POST",
    json: { activation_id: input.activationId, attendee_ref: input.attendeeRef },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const ride = fields(fields(fields(answer.body)["erasure"])["ride"]);
  const state = ride["state"];
  if (typeof state !== "string") return { ok: false, ...unreadable("erasure follow-up", "erasure.ride.state") };
  return { ok: true, value: { rideState: state } };
}

/**
 * What the service records when a session publishes and the body names nobody.
 *
 * A category, never an email. publish/routes.ts writes the same string into
 * opened_by when a session omits a name; the bundle field is required rather
 * than optional, so the console sends the category rather than inventing a
 * person to satisfy a claim column.
 */
export const SESSION_PUBLISHED_CLAIM = "a_signed_in_nova_admin";

export type PublishedBundle = {
  readonly activationId: string;
  readonly clientId: string;
  readonly version: number;
  readonly issuedAt: string;
  readonly instructionId: string;
};

/**
 * Sign and publish a configuration bundle.
 *
 * The bundle is assembled by compileConfigBundle, not here. This function
 * posts the artefact and the six digits. It does not hold XP_ADMIN_TOKEN.
 */
export async function publishConfigBundle(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    clientId: string;
    bundle: Record<string, unknown>;
    secondFactorCode: string;
    consentWording?: readonly {
      consent_version_id: string;
      purpose: string;
      locale: string;
      body: string;
    }[];
    attestationWording?: readonly {
      attestation_version_id: string;
      kind: string;
      locale: string;
      body: string;
    }[];
  },
): Promise<Attempt<PublishedBundle>> {
  const answer = await authenticated(options, token, {
    path: PUBLISH_ENDPOINTS.publishBundle,
    method: "POST",
    json: {
      activation_id: input.activationId,
      client_id: input.clientId,
      published_by: SESSION_PUBLISHED_CLAIM,
      bundle: input.bundle,
      ...(input.consentWording === undefined || input.consentWording.length === 0
        ? {}
        : { consent_wording: input.consentWording }),
      ...(input.attestationWording === undefined || input.attestationWording.length === 0
        ? {}
        : { attestation_wording: input.attestationWording }),
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const activationId = text(b["activation_id"]);
  const clientId = text(b["client_id"]);
  const version = whole(b["version"]);
  const issuedAt = text(b["issued_at"]);
  const instruction = fields(b["instruction"] ?? {});
  const instructionId = text(instruction["instruction_id"]);
  if (
    activationId === null ||
    clientId === null ||
    version === null ||
    issuedAt === null ||
    instructionId === null
  ) {
    return { ok: false, ...unreadable("bundle publication", "activation_id, client_id, version, issued_at and instruction") };
  }
  return {
    ok: true,
    value: { activationId, clientId, version, issuedAt, instructionId },
  };
}

/**
 * What a refusal of one of these writes means.
 *
 * Wider than client.ts's `Failure` and deliberately not a superset of it: the
 * two vocabularies answer different questions, and a screen that took either
 * would have to handle kinds that cannot reach it.
 */
export type AdminRefusal =
  /** The session is not live. The caller clears it, exactly as a report 401 does. */
  | { readonly kind: "session_ended"; readonly detail: string }
  /**
   * A code has to be presented and none was. The session is untouched and the
   * person types six digits.
   */
  | { readonly kind: "code_required"; readonly detail: string }
  /**
   * The code did not verify, or had already been spent. One outcome for both,
   * because the service sends one answer for both — login.ts's reasoning, quoted
   * in api/enrol.ts: telling somebody a replayed code was replayed tells them
   * they had the right digits and the wrong moment.
   */
  | { readonly kind: "code_refused"; readonly detail: string }
  /**
   * This service cannot check a code at all — no sealing key, or a factor it
   * cannot open. Its own outcome because no button here resolves it and the
   * person should stop retyping.
   */
  | { readonly kind: "cannot_check"; readonly detail: string }
  /** Live credential, and this operation is not for it. A dead end, drawn as one. */
  | { readonly kind: "not_permitted"; readonly detail: string }
  /**
   * The route is not registered on this instance.
   *
   * The kiosk admin routes exist only where there is a signing key — app.ts
   * registers them from the same null that arms the bundle signer — so on a
   * service that has never signed anything they are Fastify's own 404. That is a
   * deployment fact rather than anything about the request, and it is the state
   * a production instance is deliberately in.
   */
  | { readonly kind: "not_offered"; readonly detail: string }
  /** Somebody else moved the thing this was about. Read again and decide again. */
  | { readonly kind: "conflict"; readonly detail: string }
  /** The service will not carry a body this size. Its own outcome so the sentence can name the file. */
  | { readonly kind: "too_large"; readonly detail: string }
  /**
   * We asked wrongly, which is this console's bug.
   *
   * `issues` is carried rather than flattened into the sentence because the
   * service sends one entry per fault on purpose — kiosk/routes.ts: "a publisher
   * who is told one fault per attempt publishes four times, and the fourth
   * attempt is the one made at eight in the morning on the day."
   */
  | { readonly kind: "malformed"; readonly detail: string; readonly issues: readonly string[] }
  | { readonly kind: "unavailable"; readonly detail: string }
  | { readonly kind: "unreachable"; readonly detail: string };

export type Attempt<T> = { readonly ok: true; readonly value: T } | ({ readonly ok: false } & AdminRefusal);

// ------------------------------------------------------------------- a tablet

export type EnrolmentCode = {
  readonly codeId: string;
  /** The only place this ever exists in plain. identity/routes.ts hashes it on the way in. */
  readonly code: string;
  readonly expiresAt: string;
  readonly activationId: string;
  /** Null for an attendee-device surface, which has no station. */
  readonly stationId: string | null;
};

/**
 * Issue a code a crew member types into a tablet.
 *
 * `stationId` is null rather than absent for the attendee surface, matching the
 * schema, which takes `v.nullable` rather than `v.optional` — the two are
 * different requests and the service distinguishes them.
 */
export async function issueEnrolmentCode(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    stationId: string | null;
    issuedBy: string;
    secondFactorCode: string;
  },
): Promise<Attempt<EnrolmentCode>> {
  const answer = await authenticated(options, token, {
    path: ADMIN_ENDPOINTS.issueEnrolmentCode,
    method: "POST",
    json: {
      activation_id: input.activationId,
      station_id: input.stationId,
      issued_by: input.issuedBy,
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });

  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const codeId = text(b["code_id"]);
  const code = text(b["code"]);
  const expiresAt = text(b["expires_at"]);
  if (codeId === null || code === null || expiresAt === null) {
    return { ok: false, ...unreadable("enrolment code", "code_id, code and expires_at") };
  }

  return {
    ok: true,
    value: {
      codeId,
      code,
      expiresAt,
      activationId: text(b["activation_id"]) ?? input.activationId,
      stationId: text(b["station_id"]),
    },
  };
}

// -------------------------------------------------------------------- a picture

export type StoredAsset = {
  /** The sha-256 of the bytes. It is the id, the url and the capability all at once. */
  readonly assetId: string;
  /** Where a screen fetches it. The service's own path, not one built here. */
  readonly src: string;
  readonly contentType: string;
  readonly byteSize: number;
  /**
   * Whether the service already held these exact bytes.
   *
   * Not a failure and drawn as an ordinary outcome, because the key is the hash:
   * kiosk/routes.ts calls a conflict here "not a fault, and the only place in
   * this file that is true". Re-uploading after a reload has to be uneventful.
   */
  readonly alreadyHeld: boolean;
};

export async function uploadKioskAsset(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    contentType: string;
    bytesBase64: string;
    /**
     * What this console believes the bytes hash to, when it could work it out.
     *
     * Sent when known and never required. The service computes the id from the
     * bytes regardless and compares: it exists so that a truncated upload is
     * found here rather than found as a broken image on a wall.
     */
    sha256: string | null;
    uploadedBy: string;
    secondFactorCode: string;
  },
): Promise<Attempt<StoredAsset>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.uploadAsset,
    method: "POST",
    json: {
      activation_id: input.activationId,
      content_type: input.contentType,
      bytes_base64: input.bytesBase64,
      ...(input.sha256 === null ? {} : { sha256: input.sha256 }),
      uploaded_by: input.uploadedBy,
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });

  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const assetId = text(b["asset_id"]);
  const src = text(b["src"]);
  if (assetId === null || src === null) {
    return { ok: false, ...unreadable("upload", "asset_id and src") };
  }

  return {
    ok: true,
    value: {
      assetId,
      src,
      contentType: text(b["content_type"]) ?? input.contentType,
      byteSize: whole(b["byte_size"]) ?? 0,
      alreadyHeld: b["already_held"] === true,
    },
  };
}

export type FilmPlan = {
  readonly filmId: string;
  readonly src: string;
  readonly uploadUrl: string | null;
  readonly contentType: string;
  readonly byteSize: number;
  readonly alreadyHeld: boolean;
};

export type StoredFilm = {
  readonly filmId: string;
  readonly src: string;
  readonly contentType: string;
  readonly byteSize: number;
  readonly posterSrc: string | null;
};

export async function startKioskFilm(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    contentType: string;
    byteSize: number;
    sha256: string;
    uploadedBy: string;
    secondFactorCode: string;
  },
): Promise<Attempt<FilmPlan>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.startFilm,
    method: "POST",
    json: {
      activation_id: input.activationId,
      content_type: input.contentType,
      byte_size: input.byteSize,
      sha256: input.sha256,
      uploaded_by: input.uploadedBy,
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });

  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const filmId = text(b["film_id"]);
  const src = text(b["src"]);
  if (filmId === null || src === null) {
    return { ok: false, ...unreadable("film", "film_id and src") };
  }

  return {
    ok: true,
    value: {
      filmId,
      src,
      uploadUrl: text(b["upload_url"]),
      contentType: text(b["content_type"]) ?? input.contentType,
      byteSize: whole(b["byte_size"]) ?? input.byteSize,
      alreadyHeld: b["already_held"] === true,
    },
  };
}

export async function finishKioskFilm(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    contentType: string;
    byteSize: number;
    sha256: string;
    secondFactorCode: string;
    posterBase64?: string | undefined;
  },
): Promise<Attempt<StoredFilm>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.finishFilm,
    method: "POST",
    json: {
      activation_id: input.activationId,
      content_type: input.contentType,
      byte_size: input.byteSize,
      sha256: input.sha256,
      ...(input.posterBase64 === undefined ? {} : { poster_base64: input.posterBase64 }),
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });

  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const filmId = text(b["film_id"]);
  const src = text(b["src"]);
  if (filmId === null || src === null) {
    return { ok: false, ...unreadable("film", "film_id and src") };
  }

  return {
    ok: true,
    value: {
      filmId,
      src,
      contentType: text(b["content_type"]) ?? input.contentType,
      byteSize: whole(b["byte_size"]) ?? input.byteSize,
      posterSrc: text(b["poster_src"]),
    },
  };
}

export async function startKioskPicture(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    contentType: string;
    byteSize: number;
    sha256: string;
    uploadedBy: string;
    secondFactorCode: string;
  },
): Promise<Attempt<FilmPlan>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.startPicture,
    method: "POST",
    json: {
      activation_id: input.activationId,
      content_type: input.contentType,
      byte_size: input.byteSize,
      sha256: input.sha256,
      uploaded_by: input.uploadedBy,
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });

  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const assetId = text(b["asset_id"]);
  const src = text(b["src"]);
  if (assetId === null || src === null) {
    return { ok: false, ...unreadable("picture", "asset_id and src") };
  }

  return {
    ok: true,
    value: {
      filmId: assetId,
      src,
      uploadUrl: text(b["upload_url"]),
      contentType: text(b["content_type"]) ?? input.contentType,
      byteSize: whole(b["byte_size"]) ?? input.byteSize,
      alreadyHeld: b["already_held"] === true,
    },
  };
}

export async function finishKioskPicture(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    contentType: string;
    byteSize: number;
    sha256: string;
    secondFactorCode: string;
  },
): Promise<Attempt<StoredAsset>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.finishPicture,
    method: "POST",
    json: {
      activation_id: input.activationId,
      content_type: input.contentType,
      byte_size: input.byteSize,
      sha256: input.sha256,
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });

  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const assetId = text(b["asset_id"]);
  const src = text(b["src"]);
  if (assetId === null || src === null) {
    return { ok: false, ...unreadable("picture", "asset_id and src") };
  }

  return {
    ok: true,
    value: {
      assetId,
      src,
      contentType: text(b["content_type"]) ?? input.contentType,
      byteSize: whole(b["byte_size"]) ?? input.byteSize,
      alreadyHeld: b["already_held"] === true,
    },
  };
}

/**
 * The bytes themselves, to the address start returned.
 *
 * Not authenticated and not our origin: the service minted a one-shot GCS
 * upload so this console never posts tens of megabytes through Cloud Run.
 */
export async function putFilmBytes(uploadUrl: string, file: File): Promise<{ ok: true } | { ok: false; detail: string }> {
  try {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "content-type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!response.ok) {
      return { ok: false, detail: `The film store answered ${response.status}. Nothing was saved on the video item.` };
    }
    return { ok: true };
  } catch {
    return { ok: false, detail: "This browser could not reach the film store. Nothing was saved on the video item." };
  }
}

// ------------------------------------------------------------------- a schedule

export type Published = {
  readonly activationId: string;
  readonly version: number;
  readonly publishedAt: string;
  /**
   * Sources this signature does not reach, handed back by the service so a
   * console can show them.
   *
   * An https address is outside everything signed: its bytes can change after
   * publication with no version moving anywhere. Carried through rather than
   * dropped, because the alternative is a screen that says "published" about a
   * rotation half of which a captive portal can substitute.
   */
  readonly unsignedSources: readonly string[];
  /** How long before a screen has it, in the service's own number. */
  readonly onScreenWithinMs: number | null;
};

/**
 * The module's document minus the version it guessed.
 *
 * `publish` stamps `previousVersion + 1` so `readSchedule` will accept the
 * object. This service then refuses `schedule.version`, allocates the number,
 * and writes it in. Forwarding the guess is what made every first publish fail
 * with "the console asked for that wrongly" and left the wall waiting.
 */
function scheduleWithoutAllocatedVersion(schedule: Record<string, unknown>): Record<string, unknown> {
  const outgoing = { ...schedule };
  delete outgoing["version"];
  return outgoing;
}

/**
 * Sign and publish a schedule.
 *
 * `schedule` is `publication.json` as the kiosk module produced it. This
 * function takes that artefact rather than a shape assembled at the call site,
 * so there is nowhere between the module and the wire for a console to invent
 * items, windows or sources.
 *
 * The one field that leaves is `version`. The module puts one on the document
 * so the player's own reader will accept it. The service then refuses a caller
 * who names one, allocates the real number inside the transaction, and stamps
 * it. A console that forwarded the guess was refused on every first publish,
 * with nothing written and a wall still waiting.
 */
export async function publishKioskSchedule(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    clientId: string;
    publishedBy: string;
    schedule: Record<string, unknown>;
    secondFactorCode: string;
  },
): Promise<Attempt<Published>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.publishSchedule,
    method: "POST",
    json: {
      activation_id: input.activationId,
      client_id: input.clientId,
      published_by: input.publishedBy,
      schedule: scheduleWithoutAllocatedVersion(input.schedule),
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });

  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const version = whole(b["version"]);
  const publishedAt = text(b["published_at"]);
  if (version === null || publishedAt === null) {
    return { ok: false, ...unreadable("publication", "version and published_at") };
  }

  return {
    ok: true,
    value: {
      activationId: text(b["activation_id"]) ?? input.activationId,
      version,
      publishedAt,
      unsignedSources: strings(b["unsigned_sources"]),
      onScreenWithinMs: whole(b["on_screen_within_ms"]),
    },
  };
}

// ----------------------------------------------------------- which screens spoke

export type SeenScreen = {
  readonly screenIdClaimed: string;
  readonly lastSeenAt: string;
  /**
   * What that browser said it was running, or null if it has not said a version.
   *
   * An integer or absent. A float, a sentence, or anything else is treated as
   * unsaid rather than displayed as a version — the same rule as the count.
   */
  readonly runningVersion: number | null;
  /**
   * How many items of the rotation were in trouble when it last spoke.
   *
   * An integer ≥ 0, or null when the wire did not send one. Null is drawn as
   * "not a count" rather than as zero: zero is a claim the screen made, and a
   * sentence that failed to parse is not that claim.
   */
  readonly troubledCount: number | null;
  /**
   * Latest signed removal sequence this screen says it has honoured.
   *
   * Zero means no removal list. Null means an older service did not send the
   * field, and must not be drawn as zero.
   */
  readonly appliedRemovalSequence: number | null;
};

export type SeenScreens = {
  readonly activationId: string;
  readonly publishedVersion: number | null;
  /** Latest removal sequence written for this activation; zero means none. */
  readonly currentRemovalSequence: number | null;
  readonly screens: readonly SeenScreen[];
};

/**
 * Which browsers have spoken for this activation, and the trouble count each
 * last sent.
 *
 * A session GET, like listing clients. No authenticator code: this is a
 * diagnostic integer, not a publish. The client is a parameter rather than
 * looked up, matching kiosk/routes.ts, so a mistyped activation is not enough
 * to read another client's screens.
 */
export async function readKioskScreens(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; clientId: string },
  /** The column the reader pressed on Connected screens, or null for newest check-in first. */
  order: { readonly key: string; readonly dir: "asc" | "desc" } | null = null,
): Promise<Attempt<SeenScreens>> {
  const answer = await authenticated(options, token, {
    path:
      `${KIOSK_ENDPOINTS.seenScreens}` +
      `?activation_id=${encodeURIComponent(input.activationId)}` +
      `&client_id=${encodeURIComponent(input.clientId)}` +
      (order === null ? "" : `&sort=${encodeURIComponent(order.key)}&dir=${order.dir === "desc" ? "desc" : "asc"}`),
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const raw = b["screens"];
  if (!Array.isArray(raw)) return { ok: false, ...unreadable("list of screens", "screens") };

  const screens: SeenScreen[] = [];
  for (const entry of raw) {
    const row = fields(entry);
    const screenIdClaimed = text(row["screen_id_claimed"]);
    const lastSeenAt = text(row["last_seen_at"]);
    if (screenIdClaimed === null || lastSeenAt === null) {
      return { ok: false, ...unreadable("list of screens", "screen_id_claimed and last_seen_at") };
    }
    // Known fields only. A reason, a URL or an item id on the row is dropped
    // rather than carried: the desk must not be able to display what the wire
    // was forbidden to hold.
    screens.push({
      screenIdClaimed,
      lastSeenAt,
      runningVersion: integerOrNull(row["running_version"]),
      troubledCount: integerCount(row["troubled_count"]),
      appliedRemovalSequence: integerCount(row["applied_removal_sequence"]),
    });
  }

  return {
    ok: true,
    value: {
      activationId: text(b["activation_id"]) ?? input.activationId,
      publishedVersion: integerOrNull(b["published_version"]),
      currentRemovalSequence: integerCount(b["current_removal_sequence"]),
      screens,
    },
  };
}

// -------------------------------------------------------------- the playlist

export type KioskPlaylist = {
  readonly cycleMs: number;
  readonly items: readonly Record<string, unknown>[];
  readonly assets: readonly StoredAsset[];
  readonly recordedAt: string | null;
  readonly source: "saved" | "published" | "empty";
  readonly sound: boolean;
  readonly syncTiming: boolean | undefined;
};

/**
 * The working rotation for this activation.
 *
 * A session GET, like listing clients. No authenticator code: this is the
 * playlist Content edits, not a publish. The client is a parameter rather
 * than looked up, matching kiosk/routes.ts.
 */
export async function readKioskPlaylist(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; clientId: string },
): Promise<Attempt<KioskPlaylist>> {
  const answer = await authenticated(options, token, {
    path:
      `${KIOSK_ENDPOINTS.playlist}` +
      `?activation_id=${encodeURIComponent(input.activationId)}` +
      `&client_id=${encodeURIComponent(input.clientId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  return playlistFrom(answer.body);
}

/**
 * Store the working rotation. Newest row wins. Does not put anything on a wall.
 */
export async function saveKioskPlaylist(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    clientId: string;
    cycleMs: number;
    items: readonly Record<string, unknown>[];
    assets: readonly StoredAsset[];
    sound: boolean;
    syncTiming: boolean;
  },
): Promise<Attempt<KioskPlaylist>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.playlist,
    method: "PUT",
    json: {
      activation_id: input.activationId,
      client_id: input.clientId,
      playlist: {
        version: 1,
        cycle_ms: input.cycleMs,
        sound: input.sound,
        sync_timing: input.syncTiming,
        items: input.items,
        assets: input.assets.map((asset) => ({
          asset_id: asset.assetId,
          src: asset.src,
          content_type: asset.contentType,
          byte_size: asset.byteSize,
        })),
      },
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  return playlistFrom(answer.body);
}

function playlistFrom(body: unknown): Attempt<KioskPlaylist> {
  const b = fields(body);
  const raw = fields(b["playlist"]);
  const items = raw["items"];
  if (!Array.isArray(items)) return { ok: false, ...unreadable("playlist", "items") };
  const cycleMs = whole(raw["cycle_ms"]);
  if (cycleMs === null) return { ok: false, ...unreadable("playlist", "cycle_ms") };

  const assets: StoredAsset[] = [];
  if (Array.isArray(raw["assets"])) {
    for (const entry of raw["assets"]) {
      const row = fields(entry);
      const assetId = text(row["asset_id"]);
      const src = text(row["src"]);
      if (assetId === null || src === null) continue;
      assets.push({
        assetId,
        src,
        contentType: text(row["content_type"]) ?? "application/octet-stream",
        byteSize: whole(row["byte_size"]) ?? 0,
        alreadyHeld: true,
      });
    }
  }

  const source = rawSource(b["source"]);
  return {
    ok: true,
    value: {
      cycleMs,
      items: items.filter((item): item is Record<string, unknown> => item !== null && typeof item === "object" && !Array.isArray(item)),
      assets,
      recordedAt: text(b["recorded_at"]),
      source,
      sound: raw["sound"] === true,
      syncTiming: raw["sync_timing"] === true ? true : raw["sync_timing"] === false ? false : undefined,
    },
  };
}

function rawSource(value: unknown): KioskPlaylist["source"] {
  return value === "saved" || value === "published" ? value : "empty";
}

// ---------------------------------------------------------------------- a token

export type ScreenToken = {
  readonly activationId: string;
  readonly purpose: "view" | "pull";
  /**
   * Shown once. Nothing reads it back and nothing here keeps it.
   *
   * A STRING, AND NOT THE `SessionToken` HANDLE THE REST OF THIS FILE NOW TAKES.
   * That is the distinction rotation turns on and it is worth one sentence
   * here, because the two are one character apart at a call site. A screen token
   * is minted for a device in a hall that polls a url and cannot be handed a
   * replacement mid-shift; it is revoked individually rather than rotated, and
   * api/src/reports/auth.ts's header sets out why. It is displayed, which is the
   * exact thing session/store.ts says a rotating token must never be.
   */
  readonly token: string;
  readonly issuedAt: string;
  /**
   * The service's own instruction about where this belongs.
   *
   * Drawn rather than paraphrased, and it is the most load-bearing sentence on
   * that screen: it is what says a view token goes after the `#` and never in
   * the query string, because a query string is in every venue proxy's access
   * log. A console that summarised it would drop the half that matters.
   */
  readonly how: string;
};

export async function issueScreenToken(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    clientId: string;
    purpose: "view" | "pull";
    issuedBy: string;
    secondFactorCode: string;
  },
): Promise<Attempt<ScreenToken>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.issueScreenToken,
    method: "POST",
    json: {
      activation_id: input.activationId,
      client_id: input.clientId,
      purpose: input.purpose,
      issued_by: input.issuedBy,
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });

  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const minted = text(b["token"]);
  const issuedAt = text(b["issued_at"]);
  const how = text(b["how"]);
  if (minted === null || issuedAt === null || how === null) {
    // The instruction is required rather than defaulted, unlike every other
    // sentence in this file. A view token drawn without "put this after the #"
    // is a credential somebody puts in a query string, and this console has no
    // business inventing that sentence from memory when the service owns it.
    return { ok: false, ...unreadable("screen token", "token, issued_at and how") };
  }

  return {
    ok: true,
    value: {
      activationId: text(b["activation_id"]) ?? input.activationId,
      purpose: b["purpose"] === "pull" ? "pull" : "view",
      token: minted,
      issuedAt,
      how,
    },
  };
}

export type PreviewScreenToken = {
  readonly activationId: string;
  readonly token: string;
  readonly expiresAt: string;
};

/** A short-lived view credential for Console's Test this piece links. */
export async function issuePreviewScreenToken(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; clientId: string },
): Promise<Attempt<PreviewScreenToken>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.issuePreviewToken,
    method: "POST",
    json: { activation_id: input.activationId, client_id: input.clientId },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const b = fields(answer.body);
  const minted = text(b["token"]);
  const expiresAt = text(b["expires_at"]);
  if (minted === null || expiresAt === null) {
    return { ok: false, ...unreadable("preview screen token", "token and expires_at") };
  }
  return {
    ok: true,
    value: {
      activationId: text(b["activation_id"]) ?? input.activationId,
      token: minted,
      expiresAt,
    },
  };
}

// ----------------------------------------------------------- what is pulled

export type HeldRemovals = {
  readonly activationId: string;
  readonly sequence: number;
  readonly items: readonly string[];
  readonly entries: readonly string[];
};

/**
 * How long a hosted film is, read on this service.
 *
 * The desk cannot open a client's CDN. Together still needs one number every
 * wall shares, so the length is asked here and written onto the piece.
 */
export async function readKioskFilmLength(
  options: ApiOptions,
  token: SessionToken,
  url: string,
): Promise<Attempt<{ readonly seconds: number }>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.filmLength,
    method: "POST",
    json: { url },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const seconds = whole(fields(answer.body)["seconds"]);
  if (seconds === null || seconds <= 0) return { ok: false, ...unreadable("film length", "seconds") };
  return { ok: true, value: { seconds } };
}

export async function readKioskRemovals(
  options: ApiOptions,
  token: SessionToken,
  input: { activationId: string; clientId: string },
): Promise<Attempt<HeldRemovals>> {
  const answer = await authenticated(options, token, {
    path:
      `${KIOSK_ENDPOINTS.adminRemovals}` +
      `?activation_id=${encodeURIComponent(input.activationId)}` +
      `&client_id=${encodeURIComponent(input.clientId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const b = fields(answer.body);
  const sequence = whole(b["sequence"]);
  if (sequence === null) return { ok: false, ...unreadable("removals", "sequence") };
  return {
    ok: true,
    value: {
      activationId: text(b["activation_id"]) ?? input.activationId,
      sequence,
      items: strings(b["items"]),
      entries: strings(b["entries"]),
    },
  };
}

export type ChangedRemovals = HeldRemovals & {
  readonly onScreenWithinMs: number | null;
};

export async function changeKioskRemovals(
  options: ApiOptions,
  token: SessionToken,
  input: {
    activationId: string;
    clientId: string;
    actedBy: string;
    secondFactorCode: string;
    pullItems?: readonly string[];
    restoreItems?: readonly string[];
    pullEntries?: readonly string[];
    restoreEntries?: readonly string[];
  },
): Promise<Attempt<ChangedRemovals>> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.adminRemovals,
    method: "POST",
    json: {
      activation_id: input.activationId,
      client_id: input.clientId,
      acted_by: input.actedBy,
      pull_items: input.pullItems,
      restore_items: input.restoreItems,
      pull_entries: input.pullEntries,
      restore_entries: input.restoreEntries,
      [FRESH_FACTOR_FIELD]: input.secondFactorCode,
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const b = fields(answer.body);
  const sequence = whole(b["sequence"]);
  const document = fields(b["document"]);
  if (sequence === null) return { ok: false, ...unreadable("removals", "sequence") };
  return {
    ok: true,
    value: {
      activationId: text(b["activation_id"]) ?? input.activationId,
      sequence,
      items: strings(document["items"] ?? b["items"]),
      entries: strings(document["entries"] ?? b["entries"]),
      onScreenWithinMs: whole(b["on_screen_within_ms"]),
    },
  };
}

// ------------------------------------------------------------------- the reading

/**
 * What a refusal of any of the four means.
 *
 * Reads `error` before `status` wherever the two disagree, which on this surface
 * is every 403 — see the header.
 *
 * PREFIXED RATHER THAN CALLED `refusalFor`, WHICH IT WAS UNTIL THE CHECK HARNESS
 * SAID OTHERWISE. api/enrol.ts already has a `refusalFor` and api/client.ts a
 * `failureFor`, and the three answer different questions over different
 * vocabularies. The collision was not a compile error — nothing imports two of
 * them — and it surfaced as an `export *` in check.mjs's generated door quietly
 * resolving to nothing, so a section calling it failed with "is not a function"
 * rather than with anything about ambiguity. Three functions one letter apart in
 * meaning are worth telling apart by name regardless of the bundler.
 */
export function adminRefusalFor(answer: Extract<Answer, { ok: false }>): AdminRefusal {
  const error = typeof answer.body?.error === "string" ? answer.body.error : null;
  const said = typeof answer.body?.detail === "string" && answer.body.detail !== "" ? answer.body.detail : null;

  if (answer.status === null) {
    return {
      kind: "unreachable",
      detail:
        "The console could not reach the service. Nothing has been sent, and nothing has changed on any " +
        "tablet or screen.",
    };
  }

  if (answer.status === 401) {
    return { kind: "session_ended", detail: said ?? "That sign-in is no longer live. Sign in again." };
  }

  if (answer.status === 403) {
    // The three factor codes, each with a different thing for a person to do.
    // Everything else on a 403 is a genuine dead end and is drawn as one.
    if (error === "second_factor_code_required") {
      return { kind: "code_required", detail: said ?? "This needs a code from your authenticator." };
    }
    if (error === "second_factor_code_wrong") {
      return {
        kind: "code_refused",
        // Verbatim, because the server's sentence covers a wrong code and a spent
        // one without saying which, and carries what to do about either.
        detail: said ?? "That code did not verify. Wait for the next one and try again.",
      };
    }
    if (error === "second_factor_unavailable") {
      return {
        kind: "cannot_check",
        detail:
          said ??
          "This service cannot check an authenticator code. That is how it is configured rather than " +
            "anything about your account.",
      };
    }
    return {
      kind: "not_permitted",
      detail: said ?? "You are signed in, and this is not something this sign-in may do.",
    };
  }

  if (answer.status === 404) {
    // Two 404s, told apart the way api/enrol.ts tells its two apart: ours carry
    // an `error` and Fastify's own do not. `activation_unknown` is a real answer
    // about a real request; a bare 404 is a route that is not registered here.
    return error === null
      ? {
          kind: "not_offered",
          detail:
            "This service does not offer that operation. The kiosk admin routes exist only where there is a " +
            "signing key, and an instance without one has no such routes at all rather than routes that " +
            "refuse. Nothing is wrong with what you typed.",
        }
      : { kind: "not_permitted", detail: said ?? "The service has no such activation." };
  }

  if (answer.status === 409) {
    return {
      kind: "conflict",
      detail:
        said ??
        "Somebody else changed this at the same moment. Nothing was written. Read it again and decide again.",
    };
  }

  if (answer.status === 413) {
    return { kind: "too_large", detail: said ?? "The service will not accept a file that size." };
  }

  if (answer.status === 400) {
    return {
      kind: "malformed",
      detail: said ?? "The console asked for that in a way the server did not accept.",
      issues: issuesIn(answer.body),
    };
  }

  return {
    kind: "unavailable",
    detail: said ?? `The service answered ${answer.status}, which this console has no sentence for.`,
  };
}

/**
 * A 200 this console could not read, refused rather than half-drawn.
 *
 * api/enrol.ts's argument about a partly-read enrolment, and it transfers to
 * these writes: an enrolment code without its expiry, a publication without its
 * version, a screen token without the sentence saying where it goes, or an
 * opened show without its name are each an answer that looks like success and
 * leaves somebody worse off than a refusal would have.
 */
function unreadable(what: string, wanted: string): AdminRefusal {
  return {
    kind: "unreachable",
    detail:
      `The service accepted that and answered with something this console cannot read as ${a(what)}: ` +
      `${wanted} are what it needs. It may well have worked, so do not repeat it blindly. Check before ` +
      `assuming either way.`,
  };
}

const a = (what: string) => (/^[aeiou]/.test(what) ? `an ${what}` : `a ${what}`);

const fields = (body: unknown): Record<string, unknown> =>
  typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};

const text = (value: unknown): string | null => (typeof value === "string" && value !== "" ? value : null);

const whole = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

/**
 * A whole number, or null. `whole` accepts 1.5; this does not.
 *
 * The trouble column is an integer ≥ 0. Displaying a float or a sentence as a
 * count would be the desk inventing the one thing the wire was built not to
 * carry. Null here is "not a count", and is drawn as that rather than as zero.
 */
const integerCount = (value: unknown): number | null =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : null;

/** A version, which may be null, or null where the wire did not send an integer. */
const integerOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  return typeof value === "number" && Number.isInteger(value) ? value : null;
};

const strings = (value: unknown): readonly string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];

/** A list the wire may omit. Absent and unreadable both come back as null. */
const stringListOrNull = (value: unknown): readonly string[] | null => {
  if (value === undefined) return null;
  if (!Array.isArray(value) || value.some((entry) => typeof entry !== "string")) return null;
  return value.filter((entry): entry is string => typeof entry === "string");
};

const booleanMapOrNull = (value: unknown): Readonly<Record<string, boolean>> | null => {
  if (value === undefined || value === null) return null;
  if (typeof value !== "object" || Array.isArray(value)) return null;
  const out: Record<string, boolean> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (entry === true || entry === false) out[key] = entry;
  }
  return out;
};

/**
 * The per-fault list a 400 carries, as sentences.
 *
 * Flattened to `path: message` here rather than in a component, because it is a
 * shape decision about somebody else's JSON and components in this package do not
 * make those.
 */
function issuesIn(body: unknown): readonly string[] {
  const raw = (body as { issues?: unknown } | null)?.issues;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const e = fields(entry);
    const message = text(e["message"]);
    if (message === null) return [];
    const path = text(e["path"]);
    return [path === null ? message : `${path}: ${message}`];
  });
}
